<template>
    <div>
        <div>
            <div class="flex items-center">
                <div class="pl-8 mr-auto">
                    <a
                        class="pt-4 flex items-center cursor-pointer trans-ease group"
                        :class="[ isSubscribed ? 'text-primary' : 'text-primary-20 hover:text-primary-80' ]"
                        @click="toggleSubscription"
                    >
                        <svg-icon class="max-w-16 max-h-16 mr-8" type="notifications"></svg-icon>
                        <div
                            class="border-b-2 border-dashed trans-ease"
                            :class="[ isSubscribed ? 'border-primary text-primary' : 'border-primary-20 text-primary-20 group-hover:border-primary-80 group-hover:text-primary-80' ]"
                        >
                            <div class="leading-none mb-2">
                                {{ isSubscribed ? this.fields.remove_myself : this.fields.add_myself }}
                            </div>
                        </div>
                    </a>
                </div>
                <div class="flex pl-24 mr-16">
                    <tooltip
                        v-for="sub in subscriptions"
                        :key="sub.user.id"
                        :content="sub.user.name"
                        class="-mr-8 w-32 h-32"
                        position="bottom"
                    >
                        <avatar
                            :image="sub.user.image_url"
                            size="small"
                            class="cursor-default border border-white"
                        >{{ sub.user.initials }}
                        </avatar>
                    </tooltip>
                </div>
                <button
                    class="w-32 h-32 flex flex-col items-center justify-center rounded-32 bg-primary-20 text-white trans-ease hover:bg-primary-80"
                    @click="manageSubscriptions">
                    <svg-icon type="add" class="max-w-16 max-h-16"></svg-icon>
                </button>
            </div>
        </div>
        <subscriber-manager :resource-title="resourceTitle" :target="target" :subs="subscriptions"
                            v-if="managingSubscriptions" @close="stopManagingSubscribers"
                            :translations="fields"/>
    </div>
</template>
<script>
    import Tooltip from "../navigation/Tooltip";
    import Avatar from "./Avatar";
    import SubscriberManager from "./SubscriberManager";
    import SvgIcon from './SvgIcon';
    import {mapState, mapActions} from "pinia";
    import {useRepo} from 'pinia-orm';
    import {useAuthenticationStore} from '../../state/modules/authentication';
    import {useNotificationsStore} from '../../state/modules/notifications';
    import * as subscriptions from "../../services/SubscriptionService";
    import Subscription from "../../models/Subscription";
    import {normalizeJson} from "../../../shared/helpers";
    import {ADD_NOTIFICATION} from "../../state/mutation-types";

    export default {
        components: {SubscriberManager, Avatar, Tooltip, SvgIcon},
        props: {
            target: Object,
            resourceTitle: String,
            type: {
                required: true,
                type: String,
                validator: val => ['applications', 'jobs', 'interviews'].includes(val)
            },
            resourceId: {
                required: true,
                type: [String, Number]
            },
            translations: Object
        },
        data() {
            return {
                managingSubscriptions: false,
            }
        },
        computed: {
            ...mapState(useAuthenticationStore, ['currentUserId']),
            subscriptions() {
                return useRepo(Subscription)
                    .with('user')
                    .where('subable_type', this.target.constructor.entity)
                    .where('subable_id', this.target.id)
                    .get()
            },
            userSubscription() {
                return this.subscriptions.find(subscription => {
                    return subscription.user_id === this.currentUserId;
                });
            },
            isSubscribed() {
                return !!this.userSubscription;
            },
            fields() {
                return Object.assign({
                    add_myself: this.$t('applications.turn_on_notifications'),
                    remove_myself: this.$t('applications.turn_off_notifications'),
                    added_myself: this.$t('applications.notifications_turned_on', { application: this.resourceTitle }),
                    removed_myself: this.$t('applications.notifications_turned_off', { application: this.resourceTitle }),
                }, this.translations)
            }
        },
        methods: {
            ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
            manageSubscriptions() {
                this.managingSubscriptions = true;
            },
            stopManagingSubscribers() {
                this.managingSubscriptions = false;
            },

            toggleSubscription() {
                if (this.isSubscribed) {
                    this.unsubscribeFrom();
                } else {
                    this.subscribeTo();
                }
            },
            subscribeTo() {
                subscriptions.store({
                    data: {
                        attributes: {},
                        relationships: {
                            user: {
                                data: {
                                    type: 'user',
                                    id: this.currentUserId,
                                }
                            },
                            subable: {
                                data: {
                                    type: this.type,
                                    id: this.resourceId,
                                }
                            }
                        }
                    }
                }).then(result => {
                    return useRepo(Subscription).save(normalizeJson(result));
                }).then(inserts => {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.fields.added_myself,
                    });
                });
            },
            unsubscribeFrom() {
                let subId = this.userSubscription.id;
                subscriptions.destroy(subId).then(() => {
                    useRepo(Subscription).destroy(subId);
                }).then(() => {
                    this[ADD_NOTIFICATION]({
                        type: 'success',
                        message: this.fields.removed_myself,
                    });
                })
            },
        }
    }
</script>
